import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import styled from 'styled-components';
import { PhoenixThemeProvider, SpinnerLoaderView } from '@airasia-phoenix/web';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { PriceChanged, HotelNotAvailableAnymore } from '../../utils/statics';
import FeedbackModal from './Modal';
import { PRECHECKOUT_TYPES, SOLD_OUT } from './constant';

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #212124bf;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;
/*
 * This is a HOC component that will be used for pre-checkout
 * it will be used to show the pop up and handle the close and submit
 * and display what pop up or error like price change will show
 */
const PreCheckout = ({
  children,
  roomSelected,
  isEnabled,
  redirect,
  preCheckout,
  selectedRate, // pass selected rate for price changed
  locale,
  ...rest
}) => {
  /*
   * make the `isRoomCardPressed` default prop for state, which tell
   * to make this function trigger if we will show pop up or not
   */
  const [precheckoutType, setPrecheckoutType] = useState(null);

  const preCheckoutIsFetching = get(preCheckout, 'isFetching', false);
  const preCheckoutIsFailed = get(preCheckout, 'isFailed', false);

  const enablePreCheckoutCall = true;
  const enableRebranding = true;

  /*
   * this effect only responsible to check what type of pre checkout
   * we will get, either price change or the without price change, error or sold out
   */
  useEffect(() => {
    // need to make sure the room is pressed, the precheckout is not failed, fetching and priceChanged
    if (
      preCheckout.priceChanged &&
      !preCheckoutIsFailed &&
      !preCheckoutIsFetching &&
      roomSelected &&
      preCheckout?.data?.type !== SOLD_OUT
    ) {
      setPrecheckoutType(PRECHECKOUT_TYPES.PRICE_CHANGED);
    }

    if (
      !preCheckout.priceChanged &&
      !isEmpty(preCheckout.data) &&
      !preCheckoutIsFailed &&
      !preCheckoutIsFetching &&
      preCheckout?.data?.type !== SOLD_OUT
    ) {
      // means, we will redirect to the trip detail page
      redirect();
    }

    if (preCheckoutIsFailed) {
      setPrecheckoutType(PRECHECKOUT_TYPES.TECH_ERROR);
    }

    return () => {
      setPrecheckoutType(null);
    };
  }, [
    preCheckout.priceChanged,
    preCheckout.data,
    preCheckoutIsFailed,
    roomSelected,
    preCheckoutIsFetching,
    redirect,
  ]);

  const localeCurrencyFormat = (
    currencyCode,
    amount,
    display,
    locale,
    noDecimalValues = false,
    decimalType,
    hideCurrency = false,
    defaultIDRLocale
  ) => {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      let formatter = '';
      let selectedLocale = '';
      if (!hideCurrency) {
        selectedLocale =
          defaultIDRLocale && currencyCode === 'IDR' ? 'id-id' : locale;
      }
      const format = {};
      format.currency = currencyCode || 'USD';
      if (hideCurrency === false) {
        format.style = 'currency';
        format.currencyDisplay = display;
      }
      if (noDecimalValues) {
        format.maximumFractionDigits = 0;
        format.minimumFractionDigits = 0;
      }

      try {
        formatter = new Intl.NumberFormat(
          selectedLocale || navigator.userLanguage || navigator.language,
          format
        );
      } catch {
        formatter = new Intl.NumberFormat(
          navigator.userLanguage || navigator.language || 'en-gb',
          {
            style: 'currency',
            currency: currencyCode || 'USD',
            currencyDisplay: display,
          }
        );
      }
      return formatter.format(amount);
    }

    return '';
  };

  /*
   * this data will handle other cases such error
   * if we dont have secondary button then just leave it blank
   */
  const precheckoutData = useMemo(() => {
    if (precheckoutType) {
      const selectedRateCurrency = get(
        selectedRate,
        'totalInclusivePrice.currency',
        ''
      );

      const selectedRateValue =
        get(selectedRate, 'totalDiscountedPrice.value', '') ||
        get(selectedRate, 'totalInclusivePrice.value', '');

      const newRoomRateCurrency = get(
        preCheckout,
        'newRoomRate.price.total_inclusive_price.currency',
        ''
      );
      const newRoomRateValue = get(
        preCheckout,
        'newRoomRate.price.total_inclusive_price.value',
        ''
      );
      return {
        [`${PRECHECKOUT_TYPES.PRICE_CHANGED}`]: {
          title: translate('precheckout.price_changed_title'),
          descriptions: [
            translate('precheckout.price_changed_description_new_price', {
              new_price: localeCurrencyFormat(
                newRoomRateCurrency,
                newRoomRateValue,
                'code',
                locale,
                true,
                null,
                false,
                false
              ),
            }),
            translate('precheckout.price_changed_description_old_price', {
              old_price: localeCurrencyFormat(
                selectedRateCurrency,
                selectedRateValue,
                'code',
                locale,
                true,
                null,
                false,
                false
              ),
            }),
          ],
          image: PriceChanged,
          primaryButton: translate('precheckout.primary_button'),
          secondaryButton: translate('precheckout.secondary_button'),
        },
        [`${PRECHECKOUT_TYPES.TECH_ERROR}`]: {
          title: translate('precheckout.errorTitle'),
          descriptions: [translate('precheckout.errorDescription')],
          image: HotelNotAvailableAnymore,
          primaryButton: translate('precheckout.refresh'),
        },
      };
    }

    return {};
  }, [precheckoutType, selectedRate, preCheckout, locale]);

  /*
   * handle the primary click either continue or anything
   */
  const primaryClick = () => {
    if (precheckoutType === PRECHECKOUT_TYPES.PRICE_CHANGED) {
      //  we will still redirect the component and inside of the trip detail we will pull
      // the new room rate, since its stored in store
      // clearing the checkout type
      setPrecheckoutType(null);
      redirect(true);
    }
    if (precheckoutType === PRECHECKOUT_TYPES.TECH_ERROR) {
      //  on click of refresh we will reload the details page
      setPrecheckoutType(null);
      document.body.style.overflow = 'auto';
      return children;
    }
  };

  /*
   * handle the secondary click either continue or anything
   */
  const secondaryClick = () => {
    if (precheckoutType === PRECHECKOUT_TYPES.PRICE_CHANGED) {
      setPrecheckoutType(null);
      document.body.style.overflow = 'auto';
      return children;
    }
  };

  /*
   * if the flag is not enabled then return the children
   * or like we will not show any pop up
   */
  if (!isEnabled) {
    return children;
  }

  return (
    <>
      {children}
      {preCheckoutIsFetching && (
        <LoaderOverlay>
          <PhoenixThemeProvider>
            <SpinnerLoaderView size="regular" color="#ffffff" />
          </PhoenixThemeProvider>
        </LoaderOverlay>
      )}
      <FeedbackModal
        isVisible={Boolean(precheckoutType !== null)}
        move={enableRebranding}
        precheckoutData={precheckoutData[precheckoutType]}
        primaryClick={primaryClick}
        secondaryClick={secondaryClick}
      />
    </>
  );
};

PreCheckout.propTypes = {
  children: PropTypes.node,
  roomSelected: PropTypes.bool.isRequired,
  isEnabled: PropTypes.bool.isRequired,
  redirect: PropTypes.func.isRequired,
  preCheckout: PropTypes.shape({
    priceChanged: PropTypes.bool,
    data: PropTypes.shape({
      type: PropTypes.string,
    }),
  }).isRequired,
  selectedRate: PropTypes.object, // Adjust type based on expected structure
  locale: PropTypes.string.isRequired,
};

export default PreCheckout;
